import { createFileRoute, Link } from "@tanstack/react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useServerPagination } from "@/hooks/useServerPagination";
import {
  hrApi,
  type EmployeeType,
  type OnboardingStatus,
  type OnboardingSummary,
} from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/onboarding/")({
  component: OnboardingListPage,
});

const STATUS_STYLES: Record<OnboardingStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  invited: "bg-blue-100 text-blue-700",
  captured: "bg-indigo-100 text-indigo-700",
  manager_approved: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

function StatusBadge({ status }: { status: OnboardingStatus }) {
  const style = STATUS_STYLES[status] ?? "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

function fullName(row: OnboardingSummary): string {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || "—";
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // CreatedAtUtc serialises without a timezone marker; treat it as UTC so the
  // rendered calendar date reflects the true instant in the viewer's locale
  // (else records created near UTC midnight show the wrong day).
  const iso = /[Z+]/.test(value.slice(10)) ? value : `${value}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined);
}

function OnboardingListPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });
  const [sortBy, setSortBy] = useState("created_at_utc");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [prevOrg, setPrevOrg] = useState(orgId);
  if (prevOrg !== orgId) {
    setPrevOrg(orgId);
    setCurrentPage(1);
    setSearchTerm("");
  }

  const search = searchTerm.trim();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["hr-onboarding"] });

  const list = useQuery({
    queryKey: ["hr-onboarding", orgId, offset, itemsPerPage, search, sortBy, sortOrder],
    queryFn: () =>
      hrApi.list(token, { offset, limit: itemsPerPage, search, sort: sortBy, order: sortOrder }),
    enabled: !!token && !!orgId,
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  useEffect(() => {
    if (list.data && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [list.data, totalPages, currentPage, setCurrentPage]);

  function handleSort(key: string) {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }

  const getSortDirection = (key: string) => (sortBy === key ? sortOrder : null);

  const onSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">HR Lite — Onboarding</h1>
          <p className="mt-2 text-lg text-slate-600">
            Validated employee onboarding, ready to push to payroll.
          </p>
        </div>
        <CreateEmployeeDialog onCreated={invalidate} />
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            onClear={() => onSearch("")}
            placeholder="Search by name or email…"
            containerClassName="flex-1 min-w-[200px]"
            data-testid="onboarding-search"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            {list.isFetching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {list.isError && (
          <p className="text-sm text-red-600">Failed to load onboarding sessions.</p>
        )}

        {list.isLoading || (!orgId && !list.isError) ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 && !list.isError ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {search
              ? "No onboarding sessions match your search."
              : "No onboarding sessions yet. Add an employee to get started."}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("last_name")}
                  onSort={() => handleSort("last_name")}
                >
                  Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("employee_type")}
                  onSort={() => handleSort("employee_type")}
                >
                  Type
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("status")}
                  onSort={() => handleSort("status")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>
                  Tax number
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("created_at_utc")}
                  onSort={() => handleSort("created_at_utc")}
                >
                  Added
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="text-right"
                >
                  Action
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid={`onboarding-${row.id}`}>
                  <TableCell className="font-medium">{fullName(row)}</TableCell>
                  <TableCell>{row.employee_type.replace(/_/g, " ")}</TableCell>
                  <TableCell>
                    <StatusBadge status={row.status} />
                    {row.push_has_error ? (
                      <span className="ml-2 text-xs font-medium text-red-600">
                        push failed
                      </span>
                    ) : row.is_pushed ? (
                      <span className="ml-2 text-xs font-medium text-green-700">
                        ✓ pushed
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.tax_number_status}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(row.created_at_utc)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to="/portal/hr/onboarding/$id"
                      params={{ id: String(row.id) }}
                      data-testid={`review-${row.id}`}
                    >
                      <Button variant="outline" size="sm">
                        Review
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {total > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={total}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(currentPage - 1)}
              onNextPage={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CreateEmployeeDialog({ onCreated }: { onCreated: () => void }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [employeeType, setEmployeeType] = useState<EmployeeType>("monthly_salaried");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const create = useMutation({
    mutationFn: () =>
      hrApi.create(token, {
        employee_type: employeeType,
        first_name: firstName || null,
        last_name: lastName || null,
        email: email || null,
      }),
    onSuccess: () => {
      toast.success("Onboarding session created");
      setOpen(false);
      setFirstName("");
      setLastName("");
      setEmail("");
      onCreated();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="add-employee">
          Add employee
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add employee</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee type</Label>
            <Select
              value={employeeType}
              onValueChange={(v) => setEmployeeType(v as EmployeeType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly_salaried">Monthly salaried</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="commission">Commission</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First name</Label>
              <Input
                id="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email (for the capture link)</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => create.mutate()}
            disabled={create.isPending}
            data-testid="create-employee-submit"
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
